/**
 * Home's own design tokens.
 *
 * Scoped deliberately: Home is the one screen the client signed off pixel by
 * pixel, and its palette (mint welcome band, warm action section, #F3F8FA
 * canvas) is not the rest of the app's. Nothing here is imported outside
 * `DashboardPage/` — the shared widget tokens in `components/widgets/tokens`
 * still own every other surface.
 */

import type React from "react"

export const homeColor = {
  canvas: "#F3F8FA",
  surface: "#FFFFFF",
  ink: "#0B1533",
  inkMuted: "#63799E",
  // #63799E measures 4.41:1 on white — fine for 14px descriptions, a hair
  // under AA for the 11-12px labels and captions. Same hue, one step darker.
  inkMutedStrong: "#5A7095",
  teal: "#00879B",
  tealHover: "#006F82",
  tealPale: "#DDF5F6",
  actionBg: "#CEF0F2",
  actionText: "#00647A",
  border: "#E1EAF1",
  progressTrack: "#EAF0F5",
  warmSection: "#FFFBF4",
  amber: "#FF9800",
  amberBg: "#FFF2DF",
  red: "#FF304B",
  redBg: "#FFE8EB",
  green: "#08BE62",
  greenBg: "#E1FBEF",
  categoryBg: "#E5F0FF",
  categoryText: "#0062D6"
} as const

export const homeWelcomeGradient =
  "linear-gradient(110deg, #DFF5F3 0%, #ECF8FA 65%, #F7FAFC 100%)"

// Inter is loaded alongside Manrope in index.html; Home opts into it without
// moving the app-wide `defaultTheme.token.fontFamily`.
export const HOME_FONT_STACK = "Inter, Arial, sans-serif"

/** Money and percentages line up column to column only with tabular figures. */
export const TABULAR: React.CSSProperties = {
  fontVariantNumeric: "tabular-nums",
  fontFeatureSettings: '"tnum" 1'
}

export const homeText = {
  welcomeTitle: { fontSize: 34, lineHeight: "41px", fontWeight: 700 },
  welcomeSubtitle: { fontSize: 16, lineHeight: "24px", fontWeight: 400 },
  sectionTitle: { fontSize: 20, lineHeight: "26px", fontWeight: 700 },
  metricValue: { fontSize: 28, lineHeight: "34px", fontWeight: 700 },
  financeValue: { fontSize: 20, lineHeight: "28px", fontWeight: 700 },
  actionTitle: { fontSize: 15, lineHeight: "21px", fontWeight: 600 },
  body: { fontSize: 14, lineHeight: "20px", fontWeight: 400 },
  button: { fontSize: 13, lineHeight: "18px", fontWeight: 600 },
  metricLabel: { fontSize: 11, lineHeight: "15px", fontWeight: 500 },
  chip: { fontSize: 11, lineHeight: "16px", fontWeight: 600 },
  table: { fontSize: 13, lineHeight: "19px", fontWeight: 400 },
  meta: { fontSize: 12, lineHeight: "17px", fontWeight: 400 }
} as const satisfies Record<string, React.CSSProperties>

export const homeSize = {
  cardRadius: 10,
  controlRadius: 6,
  cardPadding: 16,
  sectionGap: 14,
  contentPadding: 24,
  maxContentWidth: 1600,
  cardShadow: "0 4px 18px rgba(20, 65, 85, 0.04)"
} as const

export const homeCardStyle: React.CSSProperties = {
  background: homeColor.surface,
  border: `1px solid ${homeColor.border}`,
  borderRadius: homeSize.cardRadius,
  boxShadow: homeSize.cardShadow
}
